import { useState, useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/data';
import { resolveSquareEnv, isSandbox } from './squareEnv';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { logDebug } from '../../utils/logger';

interface SquarePaymentFormProps {
  amountCents: number;
  amountDisplay: string;
  // #127 slice 5: FAMILY_MEMBER_ADD / FAMILY_CHANGE_REQUEST added for the
  // member "Add a family member" payment step (127-family-requests-contract.md).
  paymentType: 'MEMBERSHIP_NEW' | 'MEMBERSHIP_RENEWAL' | 'COMPETITION_ENTRY' | 'FAMILY_MEMBER_ADD';
  entityId: string;
  entityType: 'MEMBER' | 'COMPETITION_DAY_REGISTRATION' | 'EVENT_PRE_REGISTRATION' | 'FAMILY_CHANGE_REQUEST';
  payerEmail: string;
  payerName: string;
  feeKey: string;
  authMode: 'apiKey' | 'userPool';
  resumePaymentToken?: string;
  /**
   * Additive, optional (15 Aug 2026) — consolidated multi-person competition
   * check-in payment. When present and non-empty, charges one combined
   * Square payment across every CompetitionDayRegistration id listed
   * instead of the single entityId above. entityId should still be set to
   * the first id in entityIds by the caller. Only meaningful for
   * paymentType=COMPETITION_ENTRY, entityType=COMPETITION_DAY_REGISTRATION
   * — see amplify/functions/process-payment/rules.ts's
   * validateMultiCompRegistrationTargets.
   */
  entityIds?: string[];
  onSuccess: (paymentId: string) => void;
  onFailure: (error: string) => void;
  onCancel: () => void;
  disclosureNotice?: string;
  /**
   * Backlog M10 (financial integrity). Called instead of onSuccess when the
   * card was genuinely charged but processPayment's own record-write or
   * membership/registration side-effect failed — handler.ts's
   * `RECONCILIATION_REQUIRED` status (see process-payment/handler.ts line
   * ~381: `paymentRecordWriteFailed || sideEffectFailed`).
   *
   * Before this fix, `success: true` alone triggered onSuccess for BOTH a
   * clean COMPLETED payment and a RECONCILIATION_REQUIRED one — a member
   * whose card was charged but whose membership was never actually marked
   * paid saw an identical "you're all set" screen to someone who paid
   * cleanly, with no record of anything needing follow-up on their end.
   *
   * Optional so every existing call site keeps compiling with no changes:
   * they simply keep using onSuccess for both outcomes until updated to show
   * the distinct "processing, do not retry" state.
   */
  onReconciliationRequired?: (paymentId: string) => void;
}

const ERROR_MESSAGES: Record<string, string> = {
  PAYMENT_DECLINED:
    "Your card was declined. Please check your details and try again, or select 'Pay on the Day'.",
  TOKEN_EXPIRED: 'The payment session has expired. Please refresh the page and try again.',
  SERVICE_UNAVAILABLE: "Online payment is temporarily unavailable. Please select 'Pay on the Day'.",
  AMOUNT_MISMATCH: 'The fee has changed — please refresh the page.',
  MISSING_FIELDS: 'Some required payment details are missing. Please refresh and try again.',
  INVALID_FEE_KEY: 'Invalid fee type. Please refresh and try again.',
  RECORD_WRITE_FAILED:
    'Payment processed but could not be recorded. Please note your reference and contact the club.',
  INVALID_PAYMENT_LINK:
    'This payment link is no longer valid. Please contact the club to have it reissued.',
  PAYMENT_IN_PROGRESS:
    'A payment attempt is already in progress. Please wait a moment before trying again.',
  ALREADY_PAID: 'This membership payment has already been received.',
  INVALID_PAYMENT_TARGET: 'The payment details are no longer valid. Please refresh the page.',
  PAY_ON_DAY_ONLY:
    "Online payment for this registration is temporarily unavailable. Please select 'Pay on the Day'.",
};

declare global {
  interface Window {
    Square?: any;
  }
}

const SquarePaymentForm: React.FC<SquarePaymentFormProps> = ({
  amountCents,
  amountDisplay,
  paymentType,
  entityId,
  entityIds,
  entityType,
  payerEmail,
  payerName,
  feeKey,
  authMode,
  resumePaymentToken,
  onSuccess,
  onFailure,
  onCancel,
  disclosureNotice,
  onReconciliationRequired,
}) => {
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkFailed, setSdkFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  const cardRef = useRef<any>(null);
  const applePayRef = useRef<any>(null);
  const googlePayRef = useRef<any>(null);
  const paymentsRef = useRef<any>(null);
  // Keep the same key when an outcome is uncertain. It is rotated only after a
  // definitive decline/expired source, where a genuinely new attempt is needed.
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  useEffect(() => {
    let mounted = true;

    const loadSdk = () => {
      const env = resolveSquareEnv();
      logDebug('SquarePayment', 'loadSdk starting, env:', env);
      const sdkUrl =
        env === 'production'
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';

      const existing = document.querySelector(`script[src="${sdkUrl}"]`);
      const script = (existing as HTMLScriptElement) ?? document.createElement('script');
      if (!existing) {
        script.src = sdkUrl;
        script.async = true;
        document.head.appendChild(script);
      }

      const init = async () => {
        try {
          const appId = import.meta.env.VITE_SQUARE_APPLICATION_ID || '';
          const locationId = import.meta.env.VITE_SQUARE_LOCATION_ID || '';
          logDebug('SquarePayment', 'init: appId present?', !!appId, 'locationId present?', !!locationId, 'window.Square present?', !!window.Square);
          const payments = window.Square!.payments(appId, locationId);

          // Card form. The postal code field is shown/required based on the
          // SELLER account's country, not a card option:
          //   - Production: the club's Square account is Australian, so Square
          //     does NOT show a postal code field.
          //   - Sandbox: the default test account is US-based, so a US ZIP
          //     field is shown and 4-digit AU postcodes are rejected.
          // CardOptions.postalCode is a STRING that PREFILLS the field (it does
          // not disable it). We prefill a valid US ZIP in sandbox only so test
          // payments pass; in production no postalCode is passed (field hidden).
          const cardOptions: any = {
            style: {
              '.input-container': { borderColor: '#1e40af', borderRadius: '6px' },
              '.input-container.is-focus': { borderColor: '#1d4ed8' },
              input: { fontFamily: 'inherit', color: '#111827' },
            },
          };
          if (isSandbox()) {
            cardOptions.postalCode = '94103'; // valid US ZIP — sandbox test account only
          }
          const card = await payments.card(cardOptions);
          await card.attach('#square-card-container');

          // Card is always available — mark ready immediately so wallet
          // detection (Apple/Google Pay) can never block the card form.
          // On Safari, payments.googlePay() can hang without resolving; doing
          // this first guarantees the card form still renders.
          if (mounted) {
            cardRef.current = card;
            paymentsRef.current = payments;
            setSdkReady(true);
            logDebug('SquarePayment', 'card attached, sdkReady=true');
          }

          // Apple Pay (Safari on Apple devices only)
          try {
            const request = payments.paymentRequest({
              countryCode: 'AU',
              currencyCode: 'AUD',
              total: { amount: (amountCents / 100).toFixed(2), label: 'Tumbulgum Fishing Club' },
            });
            const applePay = await payments.applePay(request);
            applePayRef.current = applePay;
            if (mounted) setApplePayAvailable(true);
          } catch (e) {
            // Apple Pay unavailable on this browser/device — log for diagnostics, do not block
            console.warn('Apple Pay unavailable:', e);
          }

          // Google Pay (Chrome on Android/desktop)
          try {
            const request = payments.paymentRequest({
              countryCode: 'AU',
              currencyCode: 'AUD',
              total: { amount: (amountCents / 100).toFixed(2), label: 'Tumbulgum Fishing Club' },
            });
            const googlePay = await payments.googlePay(request);
            await googlePay.attach('#google-pay-button');
            googlePayRef.current = googlePay;
            if (mounted) setGooglePayAvailable(true);
          } catch (e) {
            // Google Pay unavailable on this browser/device — log for diagnostics, do not block
            console.warn('Google Pay unavailable:', e);
          }
        } catch (e) {
          console.error('Square SDK init failed:', e);
          if (mounted) setSdkFailed(true);
        }
      };

      if (window.Square) {
        init();
      } else {
        script.onload = init;
        script.onerror = () => {
          if (mounted) setSdkFailed(true);
        };
      }
    };

    loadSdk();
    return () => {
      mounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sdkFailed) onCancel();
  }, [sdkFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitToken = async (token: string) => {
    setSubmitting(true);
    setError(null);
    logDebug('SquarePayment', 'submitToken: entityId=', entityId, 'amountCents=', amountCents, 'feeKey=', feeKey);
    try {
      const idempotencyKey = idempotencyKeyRef.current;
      const client = generateClient({ authMode } as any);
      const raw = await (client as any).mutations.processPayment({
        paymentToken: token,
        amountCents,
        paymentType,
        entityId,
        entityType,
        idempotencyKey,
        payerEmail,
        payerName,
        feeKey,
        resumePaymentToken,
        ...(entityIds && entityIds.length > 0 ? { entityIds } : {}),
      });
      const data = parseLambdaResponse<{
        success?: boolean;
        paymentId?: string;
        error?: string;
        status?: 'COMPLETED' | 'RECONCILIATION_REQUIRED';
      }>(raw?.data);
      logDebug('SquarePayment', 'processPayment response:', JSON.stringify(data));
      if (data?.success && data.paymentId) {
        // The card WAS charged either way — handler.ts only reaches this
        // branch after Square confirms the charge. status distinguishes
        // whether the follow-up record write / membership update also
        // succeeded (COMPLETED) or failed after the money was already taken
        // (RECONCILIATION_REQUIRED). Treating both as an identical "success"
        // was the M10 defect: a member could be charged with no record of it
        // and see the same confirmation screen as someone who paid cleanly.
        if (data.status === 'RECONCILIATION_REQUIRED') {
          if (onReconciliationRequired) {
            onReconciliationRequired(data.paymentId);
          } else {
            // Caller hasn't been updated to handle this distinctly yet —
            // preserve the previous behaviour rather than silently dropping
            // the payment confirmation, but at least surface the reference.
            console.warn('Payment RECONCILIATION_REQUIRED with no onReconciliationRequired handler', data.paymentId);
            onSuccess(data.paymentId);
          }
        } else {
          onSuccess(data.paymentId);
        }
      } else {
        const errorCode = data?.error ?? 'UNKNOWN';
        const msg =
          ERROR_MESSAGES[errorCode] ??
          'Payment could not be completed. Please try again or pay on the day.';
        setError(msg);
        // A decline/expired card source is definitive and safe to retry as a
        // new payment attempt. Unknown/service failures retain the key so
        // Square can return the original outcome rather than charge twice.
        if (errorCode === 'PAYMENT_DECLINED' || errorCode === 'TOKEN_EXPIRED') {
          idempotencyKeyRef.current = crypto.randomUUID();
        }
        onFailure(errorCode);
      }
    } catch {
      setError(ERROR_MESSAGES.SERVICE_UNAVAILABLE);
      onFailure('SERVICE_UNAVAILABLE');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logDebug('SquarePayment', 'handleCardSubmit: cardRef?', !!cardRef.current, 'submitting?', submitting);
    if (!cardRef.current || submitting) return;
    // Set submitting BEFORE tokenize(), not after it returns. tokenize() is
    // where Square's 3DS/SCA challenge runs when the issuing bank requires
    // one, and that step can take several seconds — occasionally over 10s,
    // with some card issuers hitting a documented Square SDK
    // ThreeDSMethodTimeoutError that still resolves successfully via an
    // internal fallback (see backlog #52). Until now the button kept
    // reading "Pay $X Now" with no visual change for that entire window, so
    // a member saw nothing happen and assumed the payment had failed or
    // frozen — this is the "payment felt confusing" feedback from the last
    // competition, independent of whatever Square/issuer-side cause the
    // timeout itself has.
    setSubmitting(true);
    setError(null);
    try {
      const result = await cardRef.current.tokenize();
      logDebug('SquarePayment', 'tokenize result status:', result.status, 'errors:', result.errors);
      if (result.status !== 'OK') {
        setError(
          result.errors?.[0]?.message ?? 'Card details could not be verified. Please try again.'
        );
        setSubmitting(false);
        return;
      }
      await submitToken(result.token);
    } catch (e) {
      console.error('[SquarePayment] tokenize threw:', e);
      setError('Card details could not be verified. Please try again.');
      setSubmitting(false);
    }
  };

  const handleApplePay = async () => {
    if (!applePayRef.current || submitting) return;
    try {
      const result = await applePayRef.current.tokenize();
      if (result.status !== 'OK') {
        setError(
          result.errors?.[0]?.message ?? 'Apple Pay could not be completed. Please try again.'
        );
        return;
      }
      await submitToken(result.token);
    } catch {
      // User cancelled Apple Pay sheet — no error shown
    }
  };

  const handleGooglePay = async () => {
    if (!googlePayRef.current || submitting) return;
    try {
      const result = await googlePayRef.current.tokenize();
      if (result.status !== 'OK') {
        setError(
          result.errors?.[0]?.message ?? 'Google Pay could not be completed. Please try again.'
        );
        return;
      }
      await submitToken(result.token);
    } catch {
      // User cancelled Google Pay sheet — no error shown
    }
  };

  if (sdkFailed) {
    return (
      <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 text-sm">
        Online payment is temporarily unavailable. Please select &quot;Pay on the Day&quot;.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {isSandbox() && (
        <div className="bg-amber-100 border border-amber-400 text-amber-800 rounded p-3 text-sm font-medium text-center">
          TEST ENVIRONMENT — Payments are not real. No money will be charged.
        </div>
      )}

      {disclosureNotice && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-3">
          {disclosureNotice}
        </p>
      )}

      <p className="text-sm font-medium text-gray-700">
        Amount: <span className="text-fishing-blue font-bold">{amountDisplay}</span>
      </p>

      {/* Apple Pay button — only shown in Safari on Apple devices */}
      {applePayAvailable && (
        <button
          type="button"
          onClick={handleApplePay}
          disabled={submitting}
          id="apple-pay-button"
          className="w-full h-12 rounded disabled:opacity-50"
          style={
            {
              WebkitAppearance: '-apple-pay-button' as any,
              ApplePayButtonType: 'pay',
              ApplePayButtonStyle: 'black',
            } as React.CSSProperties
          }
          aria-label="Pay with Apple Pay"
        />
      )}

      {/* Google Pay button — Square renders into this div during init.
          The container must always exist in the DOM so attach() can find it;
          it is hidden until Google Pay is confirmed available. */}
      <div
        id="google-pay-button"
        onClick={handleGooglePay}
        className={
          (googlePayAvailable ? '' : 'hidden') +
          (submitting ? ' opacity-50 pointer-events-none' : '')
        }
      />

      {/* "or pay by card" divider shown when any wallet pay is available */}
      {(applePayAvailable || googlePayAvailable) && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <div className="flex-1 h-px bg-gray-200" />
          <span>or pay by card</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
      )}

      <form onSubmit={handleCardSubmit} className="space-y-4">
        <div id="square-card-container" className="min-h-[90px]">
          {!sdkReady && (
            <div className="text-sm text-gray-500 animate-pulse">Loading payment form…</div>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!sdkReady || submitting}
          className="w-full bg-fishing-blue text-white py-2 px-4 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
        >
          {submitting ? 'Processing…' : `Pay ${amountDisplay} Now`}
        </button>
      </form>
    </div>
  );
};

export default SquarePaymentForm;
